import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { todayStr, fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, Image, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Publishing() {
  const qc = useQueryClient();
  const [date] = useState(todayStr());
  const { data, isLoading } = useQuery({ queryKey: ["publishing", date], queryFn: async () => (await api.get("/publishing", { params: { date } })).data });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: storyData } = useQuery({ queryKey: ["stories", date], queryFn: async () => (await api.get("/stories", { params: { date } })).data });

  const refresh = () => {
    ["publishing", "stories", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
  const markPosted = async (id) => { await api.put(`/content/${id}`, { status: "posted" }); toast.success("Marked posted"); refresh(); };
  const markApproved = async (id) => { await api.put(`/content/${id}`, { status: "approved" }); toast.success("Approved — ready to publish"); refresh(); };

  const [storyClient, setStoryClient] = useState("");
  const [storyCount, setStoryCount] = useState(5);
  const saveStory = async () => {
    if (!storyClient) return toast.error("Select a client");
    await api.post("/stories", { client_id: storyClient, story_count: Number(storyCount) });
    toast.success("Stories logged"); setStoryClient(""); setStoryCount(5); refresh();
  };

  const storyMap = {}; (storyData?.logs || []).forEach((l) => (storyMap[l.client_id] = l.story_count));

  if (isLoading || !data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  return (
    <div className="space-y-6" data-testid="publishing-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Publishing</h1>
        <p className="mt-1 text-sm text-zinc-500">Approved content ready to post — {fmtDate(date)}. One tap to mark posted.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4"><CheckCircle2 className="h-4 w-4 text-blue-500" /><h3 className="font-heading text-base font-semibold">Ready for Approval</h3><Badge color="blue">{(data.ready_for_approval || []).length}</Badge></div>
          <div className="space-y-2 p-5">
            {(data.ready_for_approval || []).length === 0 && <div className="py-6 text-center text-sm text-zinc-400">Nothing waiting for approval</div>}
            {(data.ready_for_approval || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border border-zinc-100 px-3 py-2" data-testid={`approve-row-${c.id}`}>
                <div><div className="text-sm font-medium">{c.title}</div><div className="text-xs text-zinc-500">{c.client_name} · {c.content_type} · by {c.completed_by || "—"}</div></div>
                <Button size="sm" variant="outline" onClick={() => markApproved(c.id)} data-testid={`mark-approved-${c.id}`}>Mark Approved</Button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4"><Send className="h-4 w-4 text-zinc-400" /><h3 className="font-heading text-base font-semibold">Ready to Publish</h3><Badge color="green">{data.to_publish.length}</Badge></div>
          <div className="space-y-2 p-5">
            {data.to_publish.length === 0 && <div className="py-6 text-center text-sm text-zinc-400">Nothing approved & waiting</div>}
            {data.to_publish.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border border-zinc-100 px-3 py-2" data-testid={`publish-row-${c.id}`}>
                <div><div className="text-sm font-medium">{c.title}</div><div className="text-xs text-zinc-500">{c.client_name} · planned {fmtDate(c.date)} · {c.platform}</div></div>
                <Button size="sm" onClick={() => markPosted(c.id)} data-testid={`mark-posted-${c.id}`}>Mark Posted</Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4"><CheckCircle2 className="h-4 w-4 text-emerald-500" /><h3 className="font-heading text-base font-semibold">Today's Posts</h3><Badge color="green">{data.posted_today.length}</Badge></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-4 py-2.5 text-left font-medium">Time</th><th className="px-4 py-2.5 text-left font-medium">Client</th>
                <th className="px-4 py-2.5 text-left font-medium">Content</th><th className="px-4 py-2.5 text-left font-medium">Delay</th><th className="px-4 py-2.5 text-left font-medium">By</th>
              </tr></thead>
              <tbody>
                {data.posted_today.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-zinc-400">No posts yet today</td></tr>}
                {data.posted_today.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-50">
                    <td className="px-4 py-2.5 font-mono text-xs">{(c.posted_at || "").slice(11, 16)}</td>
                    <td className="px-4 py-2.5">{c.client_name}</td>
                    <td className="px-4 py-2.5">{c.title}</td>
                    <td className="px-4 py-2.5">{c.posting_delay_days > 0 ? <Badge color="red">{c.posting_delay_days}d late</Badge> : <Badge color="green">On time</Badge>}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{c.posted_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card className="rounded-lg border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4"><Image className="h-4 w-4 text-zinc-400" /><h3 className="font-heading text-base font-semibold">Today's Stories</h3></div>
        <div className="p-5">
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md bg-zinc-50 p-4">
            <div className="min-w-[200px] flex-1">
              <label className="text-xs text-zinc-500">Client</label>
              <Select value={storyClient} onValueChange={setStoryClient}>
                <SelectTrigger className="mt-1 bg-white" data-testid="story-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="w-32"><label className="text-xs text-zinc-500">Stories Posted</label><Input type="number" min={0} value={storyCount} onChange={(e) => setStoryCount(e.target.value)} className="mt-1 bg-white" data-testid="story-count-input" /></div>
            <Button onClick={saveStory} data-testid="save-story-button">Save</Button>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {clients.map((c) => {
              const posted = storyMap[c.id];
              return (
                <div key={c.id} className={`flex items-center justify-between rounded-md border px-3 py-2 ${posted ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/30"}`}>
                  <span className="truncate text-sm">{c.name}</span>
                  {posted ? <Badge color="green">✅ {posted}</Badge> : <Badge color="red">Missing</Badge>}
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
}
